import { createReducer, on } from '@ngrx/store';
import * as actions from '../actions/employee-risk-products.actions';
import { IEmployeeRiskProductsResponse, IRiskProductOption, IStaticRiskProductOption } from '../models/employee-risk-products.model';

export const employeeRiskProductsFeatureKey = 'employeeRiskProducts';

export interface EmployeeRiskProductsState {
  loading: boolean;
  updating: boolean;
  updatingGroup: boolean;
  deleting: boolean;
  loadingCalculationTypeOptions: boolean;
  loadingDisplayTypeOptions: boolean;
  loadingBusinessRiskGroupOptions: boolean;
  loadingStaticRiskProductOptions: boolean;
  response: IEmployeeRiskProductsResponse;
  riskProductCalculationTypeOptions: IRiskProductOption[];
  riskProductDisplayTypeOptions: IRiskProductOption[];
  businessRiskGroupOptions: IRiskProductOption[];
  staticRiskProductOptions: IStaticRiskProductOption[];
  error: any;
  updateError: any;
  updateGroupError: any;
  deleteError: any;
  staticDataError: any;
  businessRiskGroupOptionsError: any;
}

export const initialState: EmployeeRiskProductsState = {
  loading: false,
  updating: false,
  updatingGroup: false,
  deleting: false,
  loadingCalculationTypeOptions: false,
  loadingDisplayTypeOptions: false,
  loadingBusinessRiskGroupOptions: false,
  loadingStaticRiskProductOptions: false,
  response: [],
  riskProductCalculationTypeOptions: [],
  riskProductDisplayTypeOptions: [],
  businessRiskGroupOptions: [],
  staticRiskProductOptions: [],
  error: null,
  updateError: null,
  updateGroupError: null,
  deleteError: null,
  staticDataError: null,
  businessRiskGroupOptionsError: null
};

export const reducer = createReducer(
  initialState,

  on(actions.loadEmployeeRiskProducts, state => ({
    ...state,
    loading: true,
    error: null,
    response: []
  })),

  on(actions.loadEmployeeRiskProductsSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    response: response || [],
    error: null
  })),

  on(actions.loadEmployeeRiskProductsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    response: [],
    error
  })),

  on(actions.updateEmployeeRiskGroup, state => ({
    ...state,
    updatingGroup: true,
    updateGroupError: null
  })),

  on(actions.updateEmployeeRiskGroupSuccess, state => ({
    ...state,
    updatingGroup: false,
    updateGroupError: null
  })),

  on(actions.updateEmployeeRiskGroupFailure, (state, { error }) => ({
    ...state,
    updatingGroup: false,
    updateGroupError: error
  })),

  on(actions.updateEmployeeRiskProduct, state => ({
    ...state,
    updating: true,
    updateError: null
  })),

  on(actions.updateEmployeeRiskProductSuccess, state => ({
    ...state,
    updating: false,
    updateError: null
  })),

  on(actions.updateEmployeeRiskProductFailure, (state, { error }) => ({
    ...state,
    updating: false,
    updateError: error
  })),

  on(actions.deleteEmployeeRiskProduct, state => ({
    ...state,
    deleting: true,
    deleteError: null
  })),

  on(actions.deleteEmployeeRiskProductSuccess, state => ({
    ...state,
    deleting: false,
    deleteError: null
  })),

  on(actions.deleteEmployeeRiskProductFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    deleteError: error
  })),

  on(actions.loadRiskProductCalculationTypeOptions, state => ({
    ...state,
    loadingCalculationTypeOptions: true,
    staticDataError: null
  })),

  on(actions.loadRiskProductCalculationTypeOptionsSuccess, (state, { response }) => ({
    ...state,
    loadingCalculationTypeOptions: false,
    riskProductCalculationTypeOptions: response || [],
    staticDataError: null
  })),

  on(actions.loadRiskProductCalculationTypeOptionsFailure, (state, { error }) => ({
    ...state,
    loadingCalculationTypeOptions: false,
    riskProductCalculationTypeOptions: [],
    staticDataError: error
  })),

  on(actions.loadRiskProductDisplayTypeOptions, state => ({
    ...state,
    loadingDisplayTypeOptions: true,
    staticDataError: null
  })),

  on(actions.loadRiskProductDisplayTypeOptionsSuccess, (state, { response }) => ({
    ...state,
    loadingDisplayTypeOptions: false,
    riskProductDisplayTypeOptions: response || [],
    staticDataError: null
  })),

  on(actions.loadRiskProductDisplayTypeOptionsFailure, (state, { error }) => ({
    ...state,
    loadingDisplayTypeOptions: false,
    riskProductDisplayTypeOptions: [],
    staticDataError: error
  })),

  on(actions.loadEmployeeBusinessRiskGroupOptions, state => ({
    ...state,
    loadingBusinessRiskGroupOptions: true,
    businessRiskGroupOptions: [],
    businessRiskGroupOptionsError: null
  })),

  on(actions.loadEmployeeBusinessRiskGroupOptionsSuccess, (state, { response }) => ({
    ...state,
    loadingBusinessRiskGroupOptions: false,
    businessRiskGroupOptions: response || [],
    businessRiskGroupOptionsError: null
  })),

  on(actions.loadEmployeeBusinessRiskGroupOptionsFailure, (state, { error }) => ({
    ...state,
    loadingBusinessRiskGroupOptions: false,
    businessRiskGroupOptions: [],
    businessRiskGroupOptionsError: error
  })),

  on(actions.loadStaticRiskProductOptions, state => ({
    ...state,
    loadingStaticRiskProductOptions: true
  })),

  on(actions.loadStaticRiskProductOptionsSuccess, (state, { response }) => ({
    ...state,
    loadingStaticRiskProductOptions: false,
    staticRiskProductOptions: [...(response || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
  })),

  on(actions.loadStaticRiskProductOptionsFailure, state => ({
    ...state,
    loadingStaticRiskProductOptions: false,
    staticRiskProductOptions: []
  }))
);

export const getEmployeeRiskProductsResponse = (state: EmployeeRiskProductsState) => state.response;
export const getEmployeeRiskProductsLoading = (state: EmployeeRiskProductsState) => state.loading;
export const getEmployeeRiskProductsUpdating = (state: EmployeeRiskProductsState) => state.updating;
export const getEmployeeRiskProductsUpdatingGroup = (state: EmployeeRiskProductsState) => state.updatingGroup;
export const getEmployeeRiskProductsDeleting = (state: EmployeeRiskProductsState) => state.deleting;
export const getEmployeeRiskProductsError = (state: EmployeeRiskProductsState) => state.error;
export const getEmployeeRiskProductsUpdateError = (state: EmployeeRiskProductsState) => state.updateError;
export const getEmployeeRiskProductsUpdateGroupError = (state: EmployeeRiskProductsState) => state.updateGroupError;
export const getEmployeeRiskProductsDeleteError = (state: EmployeeRiskProductsState) => state.deleteError;
export const getRiskProductCalculationTypeOptions = (state: EmployeeRiskProductsState) =>
  state.riskProductCalculationTypeOptions;
export const getRiskProductDisplayTypeOptions = (state: EmployeeRiskProductsState) =>
  state.riskProductDisplayTypeOptions;
export const getRiskProductStaticDataError = (state: EmployeeRiskProductsState) => state.staticDataError;
export const getRiskProductCalculationTypeOptionsLoading = (state: EmployeeRiskProductsState) =>
  state.loadingCalculationTypeOptions;
export const getRiskProductDisplayTypeOptionsLoading = (state: EmployeeRiskProductsState) =>
  state.loadingDisplayTypeOptions;
export const getBusinessRiskGroupOptions = (state: EmployeeRiskProductsState) => state.businessRiskGroupOptions;
export const getBusinessRiskGroupOptionsLoading = (state: EmployeeRiskProductsState) =>
  state.loadingBusinessRiskGroupOptions;
export const getBusinessRiskGroupOptionsError = (state: EmployeeRiskProductsState) => state.businessRiskGroupOptionsError;
export const getStaticRiskProductOptions = (state: EmployeeRiskProductsState) => state.staticRiskProductOptions;
export const getStaticRiskProductOptionsLoading = (state: EmployeeRiskProductsState) =>
  state.loadingStaticRiskProductOptions;
